// Optional Plus upgrade page. KnowMeno is free forever — this screen is
// reached from in-app upgrade CTAs only. Users pay immediately (no trial).
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { createCheckoutSession } from "@/lib/billing.functions";
import { PLANS, PLAN_ORDER, type PlanId } from "@/lib/plans";
import { getPendingPromo } from "@/lib/promoParam";
import { getPendingUtm } from "@/lib/utmParams";

export const Route = createFileRoute("/choose-plan")({
  head: () => ({
    meta: [
      { title: "Upgrade to Plus — KnowMeno" },
      { name: "description", content: "Unlock Plus features in KnowMeno. Pay monthly or annually — cancel anytime." },
      { property: "og:url", content: "https://knowmeno.com/choose-plan" },
    ],
    links: [{ rel: "canonical", href: "https://knowmeno.com/choose-plan" }],
  }),
  component: ChoosePlanPage,
});

const PLUM = "#4B1F4F";
const ROSE = "#FF5C7A";
const CREAM = "#FAF7F5";
const MUTED = "#6E4B71";
const LINE = "#E8D5DA";
const WH = "#ffffff";
const SANS = '"Inter", system-ui, -apple-system, sans-serif';
const SERIF = '"Cormorant Garamond", Georgia, serif';

function ChoosePlanPage() {
  const navigate = useNavigate();
  const checkout = useServerFn(createCheckoutSession);
  const [selected, setSelected] = useState<PlanId>("annual");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [userReady, setUserReady] = useState(false);

  // Require auth; bounce if no session.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        navigate({ to: "/sign-up" });
      } else {
        setUserReady(true);
      }
    });
  }, [navigate]);

  const handleContinue = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error("You're signed out. Please sign in again.");
      const promo = getPendingPromo();
      const utm = getPendingUtm();
      const { url } = await checkout({
        data: {
          plan: selected,
          accessToken,
          withTrial: false,
          flow: "choose-plan",
          promoCode: promo ?? undefined,
          utm: Object.keys(utm).length > 0 ? utm : undefined,
        },
      });
      window.location.href = url;
    } catch (e) {
      let msg = "Could not start checkout";
      if (e instanceof Response) msg = `Checkout failed (HTTP ${e.status})`;
      else if (e instanceof Error) msg = e.message;
      console.error("[choose-plan] checkout failed", e);
      setErr(msg);
      setSubmitting(false);
    }
  };

  if (!userReady) {
    return (
      <div style={{ minHeight: "100vh", background: CREAM, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SANS, color: MUTED, fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(165deg, ${CREAM} 0%, #FFE4EA 60%, #FFE4EA 100%)`, padding: "32px 16px 64px", fontFamily: SANS, color: PLUM }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <a
          href="/app"
          aria-label="Back to app"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "transparent",
            border: "none",
            color: PLUM,
            fontFamily: SANS,
            fontSize: 14,
            fontWeight: 500,
            textDecoration: "none",
            padding: "8px 4px",
            marginBottom: 8,
            cursor: "pointer",
            position: "relative",
            zIndex: 1,
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>←</span>
          <span>Back</span>
        </a>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.01em", marginBottom: 8 }}>
            <span style={{ color: PLUM }}>Know</span><span style={{ color: ROSE }}>Meno</span>
          </div>
          <h1 style={{ fontFamily: SERIF, fontSize: "clamp(32px, 5vw, 44px)", fontWeight: 600, color: PLUM, margin: 0, marginBottom: 12, lineHeight: 1.1 }}>
            Upgrade to Plus
          </h1>
          <p style={{ fontSize: 16, color: MUTED, margin: 0, lineHeight: 1.5 }}>
            KnowMeno is free forever. Upgrade to Plus to unlock insight charts, lab trends, and screening reminders. Cancel anytime.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14, marginBottom: 24 }}>
          {PLAN_ORDER.map((id) => {
            const p = PLANS[id];
            const isSel = selected === id;
            return (
              <button
                key={id}
                onClick={() => setSelected(id)}
                aria-pressed={isSel}
                aria-label={`${p.name} plan, ${p.displayPrice} ${p.cadence}${isSel ? ", selected" : ", not selected"}`}
                style={{
                  textAlign: "left",
                  background: WH,
                  border: `2px solid ${isSel ? ROSE : LINE}`,
                  borderRadius: 18,
                  padding: "20px 22px",
                  cursor: "pointer",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  transition: "border-color 0.15s ease",
                  fontFamily: SANS,
                }}
              >
                <div style={{
                  width: 22, height: 22, borderRadius: 11,
                  border: `2px solid ${isSel ? ROSE : "#CCC"}`,
                  background: isSel ? ROSE : WH,
                  flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {isSel && <span style={{ color: WH, fontSize: 14, fontWeight: 700 }}>✓</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: 17, fontWeight: 600, color: PLUM }}>{p.name}</span>
                    {p.badge && (
                      <span style={{ fontSize: 11, background: ROSE, color: WH, padding: "2px 9px", borderRadius: 999, fontWeight: 600, letterSpacing: "0.04em" }}>
                        {p.badge.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.5 }}>
                    <strong style={{ color: PLUM, fontWeight: 600 }}>{p.displayPrice}</strong> {p.cadence}
                    {p.perMonthEquivalent && <span style={{ marginLeft: 8, color: MUTED }}>({p.perMonthEquivalent})</span>}
                    {p.savings && <span style={{ marginLeft: 8, color: ROSE, fontWeight: 600 }}>{p.savings}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{
          background: WH, border: `1px solid ${LINE}`, borderRadius: 14,
          padding: "16px 18px", marginBottom: 20, fontSize: 13, color: MUTED, lineHeight: 1.6,
        }}>
          <strong style={{ color: PLUM }}>How billing works:</strong> Your card is charged{" "}
          <strong style={{ color: PLUM }}>{PLANS[selected].displayPrice}</strong> today for your{" "}
          <strong style={{ color: PLUM }}>{PLANS[selected].name.toLowerCase()}</strong> plan, then{" "}
          {PLANS[selected].cadence} after that. Cancel anytime from Settings.
        </div>

        {err && (
          <div style={{ background: "#FFE4EA", color: "#8A1F1F", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>
            {err}
          </div>
        )}

        <button
          onClick={handleContinue}
          disabled={submitting}
          style={{
            width: "100%",
            background: PLUM,
            color: WH,
            border: "none",
            borderRadius: 14,
            padding: "16px",
            fontSize: 16,
            fontWeight: 600,
            cursor: submitting ? "wait" : "pointer",
            opacity: submitting ? 0.7 : 1,
            boxShadow: "0 14px 30px -12px rgba(74,25,66,0.45)",
          }}
        >
          {submitting ? "Opening secure checkout…" : "Continue to checkout"}
        </button>

        <div style={{ marginTop: 14, textAlign: "center", fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
          Have a promo code? Enter it at checkout →
        </div>

        <div style={{ marginTop: 20, padding: "14px 16px", background: "rgba(255,255,255,0.6)", borderRadius: 12, fontSize: 11, color: MUTED, lineHeight: 1.55, textAlign: "center" }}>
          Payment is processed securely by Stripe. We never see or store your card details.
          By continuing you agree to our{" "}
          <a href="/terms" style={{ color: PLUM, textDecoration: "underline" }}>Terms</a> and{" "}
          <a href="/privacy" style={{ color: PLUM, textDecoration: "underline" }}>Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
}
